export interface AuthUser {
  email: string
  name: string
  sub?: string
}

export interface TokenSet {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface AuthCredentials {
  email: string
  password: string
}

export interface AuthResult {
  tokens: TokenSet
  user: AuthUser
}

/**
 * Thrown by `IAuthProvider.login()` when the identity provider issues a
 * NEW_PASSWORD_REQUIRED challenge (e.g. Cognito FORCE_CHANGE_PASSWORD accounts).
 *
 * The caller must call `IAuthProvider.completeNewPassword()` on the same
 * provider instance to complete the flow.
 */
export class NewPasswordRequiredError extends Error {
  constructor() {
    super('New password required')
    this.name = 'NewPasswordRequiredError'
  }
}

export interface IAuthProvider {
  /**
   * Exchange username + password for a token set.
   * Resolves with tokens and the authenticated user identity.
   * Rejects with a NewPasswordRequiredError when the identity provider requires
   * a mandatory password change before the user can be authenticated.
   * Rejects with an Error whose message is safe to display to the user for all
   * other failures.
   */
  login(credentials: AuthCredentials): Promise<AuthResult>

  /**
   * Complete a NEW_PASSWORD_REQUIRED challenge issued during a prior `login()`
   * call. Must be called on the same provider instance that rejected with
   * NewPasswordRequiredError.
   *
   * Resolves with an AuthResult identical in shape to a successful `login()`.
   * Rejects with an Error whose message is safe to display to the user.
   *
   * Provider implementations that do not support this challenge (e.g.
   * KeycloakAuthProvider) MUST implement a stub that always rejects with
   * Error('Not supported').
   */
  completeNewPassword(newPassword: string): Promise<AuthResult>

  /**
   * Use the refresh token to obtain a new access token.
   * Resolves with a new TokenSet.
   * Rejects with an Error if the refresh token is expired or invalid.
   */
  refresh(refreshToken: string): Promise<TokenSet>

  /**
   * Invalidate the session on the identity provider.
   * Best-effort: resolves even if the revocation call fails
   * (the caller clears local state regardless).
   */
  logout(refreshToken: string): Promise<void>
}
